import { Button } from "@/components/ui/button";
import { useCookieStore } from "@/store/cookie";
import { X } from "lucide-react";
import { useMemo } from "react";

type CookieConsentBannerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ isOpen, onClose }) => {
  const getCookieOptions = useCookieStore((state) => state.getCookieOptions);
  const options = useMemo(() => getCookieOptions(), [getCookieOptions]);
  const isSelectionModalOpen = useCookieStore((state) => state.isSelectionModalOpen);
  const setIsSelectionModalOpen = useCookieStore((state) => state.setIsSelectionModalOpen);
  const setCookiePreferences = useCookieStore((state) => state.setCookiePreferences);
  const markConsentGiven = useCookieStore((state) => state.markConsentGiven);

  if (!isOpen || isSelectionModalOpen) {
    return null;
  }

  const handleAcceptAll = () => {
    setCookiePreferences(options.map(({ value }) => value));
    markConsentGiven();
    onClose();
  };

  const handleManage = () => {
    setIsSelectionModalOpen(true);
  };

  const handleDismiss = () => {
    onClose();
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-sm"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 sm:flex-row sm:items-center">
        <p className="subtext-x-small min-w-0 flex-1 text-muted-foreground">
          We use cookies to enhance site navigation, analyse site usage, and assist in our marketing
          efforts.{" "}
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={handleManage}
          >
            Manage
          </button>
        </p>
        <div className="flex w-full shrink-0 items-center justify-between gap-2 sm:w-auto sm:justify-end">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="xs" onClick={handleManage}>
              Choose Cookies
            </Button>
            <Button size="xs" onClick={handleAcceptAll}>
              Accept All Cookies
            </Button>
          </div>
          <button
            type="button"
            aria-label="Dismiss cookie banner"
            className="shrink-0 rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:text-foreground hover:opacity-100"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
